import {
  effectivePermissionSetHas,
  LPS_INBOUND_SECTION_ORDER,
  lpsInboundSectionReadCode,
  lpsTabReadCode,
  type LpsInboundSectionId,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzDateRangePicker, UzTabs, type UzDateRange } from '@utazzo/ui';
import { RotateCw, UserPlus } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  ASSIGNMENT_METHOD_LABEL,
  CHANNEL_LABEL,
  canBeAssigned,
  FORWARD_LABEL,
  FORWARD_TONE,
  LEAD_STATUS_LABEL,
  SOURCE_LABEL,
  formatDateTime,
  formatPhone,
} from '../labels';
import {
  useLpsConnections,
  useLpsDestinationSummary,
  useLpsLeadsList,
  useRetryLpsLead,
} from '../use-lps';

import { AssignAgentDialog } from './AssignAgentDialog';
import { BulkAssignAgentDialog } from './BulkAssignAgentDialog';
import { LpsChannelSummary } from './LpsChannelSummary';
import { LpsDestinationSummary, type LpsAssignedWindow } from './LpsDestinationSummary';
import { LpsLeadDrawer } from './LpsLeadDrawer';

import type {
  LpsForwardState,
  LpsLeadItem,
  LpsLeadListQuery,
  LpsLeadStatus,
  LpsSource,
} from '../lps.api';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';
import { canBulkAssignLeads } from '@/features/leads/bulk-assign-access';
import {
  isoDate,
  LEAD_RANGE_PRESETS,
  resolveLeadDateRange,
  type LeadDateRangePresetKey,
} from '@/features/leads/lead-date-range';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';

/** Queue sections: ad leads, the rest, or everything. 'all' = no filter. */
type LeadTypeTab = LpsInboundSectionId;

/**
 * The queue opens on New leads: duplicates are repeat enquiries the CRM
 * already has, and on first load they only bury the ones that need an agent.
 * "New + duplicates" in the Status filter brings them back. Stable object:
 * it seeds the URL-state defaults.
 */
const DEFAULT_FILTERS: Readonly<Record<string, string>> = { status: 'NEW' };

const LEAD_TYPE_LABEL: Record<LeadTypeTab, string> = {
  all: 'All',
  inorganic: 'Inorganic',
  organic: 'Organic',
};

export const LpsLeadsTab = ({ canWrite }: { canWrite: boolean }): React.JSX.Element => {
  const [openId, setOpenId] = useState<string | null>(null);
  // 2026-10-04. One read grant per section (LPS_INBOUND_SECTION_MODULES),
  // like the Marketing report: a role sees only the sections it was given,
  // and opens on the first of them. ADMIN sees all three.
  const permissions = useMyEffectivePermissions();
  const role = useCurrentUser()?.role;
  const isAdmin = role === 'ADMIN';
  // Bulk assign needs both: the tab's write grant AND an admin / sales-lead
  // role (the API checks the role too - the grant alone can go to any role).
  const canBulkAssign = canWrite && canBulkAssignLeads(role);
  const sections: LeadTypeTab[] = isAdmin
    ? [...LPS_INBOUND_SECTION_ORDER]
    : LPS_INBOUND_SECTION_ORDER.filter((s) =>
        effectivePermissionSetHas(permissions, lpsInboundSectionReadCode(s)),
      );
  const [pickedLeadType, setLeadType] = useState<LeadTypeTab | null>(null);
  const leadType: LeadTypeTab | null =
    pickedLeadType && sections.includes(pickedLeadType) ? pickedLeadType : (sections[0] ?? null);
  const [assigning, setAssigning] = useState<LpsLeadItem | null>(null);
  // Rows ticked in the grid and sent to the bulk assign dialog; null = closed.
  const [bulkAssigning, setBulkAssigning] = useState<ReadonlyArray<LpsLeadItem> | null>(null);
  // Received window. Off by default ('all'): it only narrows the queue once
  // somebody picks a range. A preset keeps its key so "Last 7 days" stays
  // relative; a calendar pick becomes 'custom'.
  const [preset, setPreset] = useState<LeadDateRangePresetKey>('all');
  const [range, setRange] = useState<UzDateRange>(() => resolveLeadDateRange('all', null, null));
  const from = isoDate(range.from);
  const to = isoDate(range.to);
  // Assigned window: when the agent took the lead, not when it arrived. The
  // grid also keeps the leads that arrived in the window and still have no
  // agent, so "today" matches the summary above. Opens on today, and it is
  // shared with the breakdown table above the grid, so changing either
  // picker moves both.
  const [assignedPreset, setAssignedPreset] = useState<LeadDateRangePresetKey>('today');
  const [assignedRange, setAssignedRange] = useState<UzDateRange>(() =>
    resolveLeadDateRange('today', null, null),
  );
  const assignedFrom = isoDate(assignedRange.from);
  const assignedTo = isoDate(assignedRange.to);
  const assignedWindow: LpsAssignedWindow = useMemo(
    () => ({
      preset: assignedPreset,
      range: assignedRange,
      onPresetSelect: (p) => {
        setAssignedPreset(p.value as LeadDateRangePresetKey);
        setAssignedRange(p.range());
      },
      onValueChange: (r) => {
        setAssignedPreset('custom');
        setAssignedRange(r);
      },
    }),
    [assignedPreset, assignedRange],
  );
  // The destination and agent filter options come from the same breakdown the
  // panel above the grid shows, so the two always offer the same names.
  const summary = useLpsDestinationSummary({});
  // The connection filter needs the connection list, which sits behind the
  // Connections tab's own permission; without it the filter is simply absent.
  const canListConnections =
    isAdmin || effectivePermissionSetHas(permissions, lpsTabReadCode('connections'));
  const connections = useLpsConnections(canListConnections);
  const retry = useRetryLpsLead();

  /** Every agent holding a lead, busiest first, with how many they hold. */
  const agentOptions = useMemo(
    () =>
      (summary.data?.agents ?? [])
        .filter((a) => a.agentId && a.agentName)
        .map((a) => ({ value: a.agentId!, label: `${a.agentName} (${a.total})` })),
    [summary.data],
  );

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      {
        id: 'source',
        label: 'Source',
        options: [
          { value: '', label: 'All sources' },
          ...Object.entries(SOURCE_LABEL).map(([value, label]) => ({ value, label })),
        ],
      },
      {
        id: 'forwardStatus',
        label: 'CRM',
        options: [
          { value: '', label: 'Any CRM state' },
          ...Object.entries(FORWARD_LABEL).map(([value, label]) => ({ value, label })),
        ],
      },
      {
        id: 'status',
        label: 'Status',
        options: [
          { value: '', label: 'New + duplicates' },
          ...Object.entries(LEAD_STATUS_LABEL).map(([value, label]) => ({ value, label })),
        ],
      },
      {
        id: 'destination',
        label: 'Destination',
        widthClass: 'w-48',
        options: [
          { value: '', label: 'All destinations' },
          ...(summary.data?.destinations ?? [])
            .filter((d) => d.destination !== null)
            .map((d) => ({ value: d.destination!, label: `${d.destination} (${d.total})` })),
          { value: 'none', label: 'No destination yet' },
        ],
      },
      {
        id: 'agentId',
        label: 'Agent',
        widthClass: 'w-48',
        options: [
          { value: '', label: 'All agents' },
          ...agentOptions,
          { value: 'none', label: 'Nobody yet' },
        ],
      },
      ...(canListConnections
        ? [
            {
              id: 'connectionId',
              label: 'Connection',
              widthClass: 'w-56',
              options: [
                { value: '', label: 'All connections' },
                ...(connections.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ],
            },
          ]
        : []),
    ],
    [agentOptions, canListConnections, connections.data, summary.data],
  );

  const columns: ListScreenColumns<LpsLeadItem> = useMemo(
    () => () => {
      const cols: ReadonlyArray<DataGridColumn<LpsLeadItem>> = [
        {
          id: 'receivedAt',
          header: 'Received',
          sortable: true,
          cell: (row) => (
            <span className="text-xs whitespace-nowrap text-zinc-700">
              {formatDateTime(row.receivedAt)}
            </span>
          ),
        },
        {
          id: 'source',
          header: 'Source',
          cell: (row) => (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-zinc-900">{SOURCE_LABEL[row.source]}</span>
              <span
                className="max-w-[180px] truncate text-[11px] text-zinc-500"
                title={row.connection.name}
              >
                {row.connection.name}
                {row.connection.deleted ? ' (deleted)' : ''}
              </span>
            </div>
          ),
        },
        {
          id: 'channel',
          header: 'Channel',
          cell: (row) => (
            <div className="flex flex-col gap-0.5">
              <UzBadge tone={row.channel.isPaid ? 'warning' : 'secondary'}>
                {CHANNEL_LABEL[row.channel.channel]}
              </UzBadge>
              {(row.campaign ?? row.channel.platform) && (
                <span
                  className="max-w-[180px] truncate text-[11px] text-zinc-500"
                  title={row.campaign ?? undefined}
                >
                  {row.campaign ?? row.channel.platform}
                </span>
              )}
            </div>
          ),
        },
        {
          id: 'person',
          header: 'Name',
          cell: (row) => (
            <span className="text-sm font-medium text-zinc-900">{row.fullName ?? '-'}</span>
          ),
        },
        {
          id: 'contact',
          header: 'Contact',
          cell: (row) => (
            <div className="flex flex-col text-xs">
              <span className="font-mono text-zinc-700">{formatPhone(row.phone)}</span>
              {row.email && (
                <span className="max-w-[200px] truncate text-zinc-500">{row.email}</span>
              )}
            </div>
          ),
        },
        {
          id: 'destination',
          header: 'Destination',
          cell: (row) => <span className="text-sm text-zinc-900">{row.destination ?? '-'}</span>,
        },
        {
          id: 'message',
          header: 'Message',
          cell: (row) =>
            row.message ? (
              <span
                className="block max-w-[260px] truncate text-xs text-zinc-600"
                title={row.message}
              >
                {row.message}
              </span>
            ) : (
              <span className="text-xs text-zinc-400">-</span>
            ),
        },
        {
          id: 'status',
          header: 'Status',
          cell: (row) => (
            <UzBadge tone={row.status === 'DUPLICATE' ? 'warning' : 'secondary'}>
              {LEAD_STATUS_LABEL[row.status]}
            </UzBadge>
          ),
        },
        {
          id: 'assignedTo',
          header: 'Assigned to',
          cell: (row) =>
            row.assignment.agentName ? (
              <div className="flex flex-col">
                <span className="text-sm text-zinc-900">{row.assignment.agentName}</span>
                {row.assignment.method && (
                  <span className="text-[11px] text-zinc-500">
                    {ASSIGNMENT_METHOD_LABEL[row.assignment.method]}
                  </span>
                )}
              </div>
            ) : canWrite && canBeAssigned(row) ? (
              <UzButton
                size="sm"
                variant="outline"
                leftIcon={<UserPlus className="size-3.5" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setAssigning(row);
                }}
              >
                Assign
              </UzButton>
            ) : (
              <span className="text-xs text-zinc-400">-</span>
            ),
        },
        {
          id: 'assignedAt',
          header: 'Assigned at',
          cell: (row) =>
            row.assignment.assignedAt ? (
              <span className="text-xs whitespace-nowrap text-zinc-700">
                {formatDateTime(row.assignment.assignedAt)}
              </span>
            ) : (
              <span className="text-xs text-zinc-400">-</span>
            ),
        },
        {
          id: 'crm',
          header: 'CRM',
          cell: (row) => {
            const f = row.forward;
            const retryable = canWrite && (f.status === 'FAILED' || f.status === 'PARKED');
            return (
              <div className="flex items-center gap-2">
                {f.status === 'FORWARDED' && f.crmLeadReference ? (
                  <span className="font-mono text-xs text-emerald-700">{f.crmLeadReference}</span>
                ) : (
                  <UzBadge tone={FORWARD_TONE[f.status]} title={f.error ?? undefined}>
                    {FORWARD_LABEL[f.status]}
                  </UzBadge>
                )}
                {retryable && (
                  <UzButton
                    size="sm"
                    variant="ghost"
                    aria-label="Retry sending to CRM"
                    tooltip="Retry sending to CRM"
                    loading={retry.isPending && retry.variables === row.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      retry.mutate(row.id);
                    }}
                  >
                    <RotateCw className="size-3.5" />
                  </UzButton>
                )}
              </div>
            );
          },
        },
      ];
      return cols;
    },
    [canWrite, retry],
  );

  if (!leadType) {
    return (
      <p className="py-8 text-center text-sm text-zinc-500">
        {permissions === undefined
          ? 'Loading…'
          : 'You have the Inbound leads tab but none of its sections (All / Inorganic / Organic). Ask an admin to grant one from Roles.'}
      </p>
    );
  }

  return (
    <>
      {/* Page-level switch: both breakdown tables and the queue below follow
          it. Content-less tabs: the tab only changes their queries. */}
      <UzTabs<LeadTypeTab>
        value={leadType}
        onValueChange={setLeadType}
        className="mb-4"
        tabs={sections.map((s) => ({ value: s, label: LEAD_TYPE_LABEL[s], content: null }))}
      />
      {/* The breakdowns and the queue are separate reads: give them the
          same breathing room the grid keeps between its own blocks. */}
      <div className="mb-6 flex flex-col gap-4">
        <LpsDestinationSummary window={assignedWindow} leadType={leadType} />
        <LpsChannelSummary window={assignedWindow} leadType={leadType} />
      </div>
      <ListScreen<LpsLeadItem, LpsLeadListQuery>
        embedded
        gridId="lps-inbound-leads"
        title="Inbound leads"
        tableLayout="auto"
        // Sits with the filter chips: it filters rows like they do.
        filtersExtra={
          <>
            {/* Two windows side by side, so each one says which date it
                filters - "Today" alone could mean either. */}
            <span className="text-[12px] whitespace-nowrap text-zinc-500">Received</span>
            <UzDateRangePicker
              value={range}
              presets={LEAD_RANGE_PRESETS}
              activePresetValue={preset === 'custom' ? null : preset}
              triggerClassName="h-[34px] text-[13px]"
              placeholder="Any time"
              onPresetSelect={(p) => {
                setPreset(p.value as LeadDateRangePresetKey);
                setRange(p.range());
              }}
              onValueChange={(r) => {
                setPreset('custom');
                setRange(r);
              }}
            />
            {/* Assigned-at window. Its own picker rather than a mode switch on
                the first one: the floor asks "today's leads, assigned when?" */}
            <span className="text-[12px] whitespace-nowrap text-zinc-500">Assigned</span>
            <UzDateRangePicker
              value={assignedRange}
              presets={LEAD_RANGE_PRESETS}
              activePresetValue={assignedPreset === 'custom' ? null : assignedPreset}
              triggerClassName="h-[34px] text-[13px]"
              placeholder="Any time"
              onPresetSelect={assignedWindow.onPresetSelect}
              onValueChange={assignedWindow.onValueChange}
            />
          </>
        }
        useList={useLpsLeadsList}
        defaultSort={{ by: 'receivedAt', dir: 'desc' }}
        defaultFilters={DEFAULT_FILTERS}
        minSearchLength={2}
        toQuery={(state, base) => {
          const f = state.filters;
          return {
            page: base.page,
            pageSize: base.pageSize,
            sortBy: 'receivedAt',
            sortDir: base.sortDir,
            ...(base.search?.trim() ? { search: base.search.trim() } : {}),
            ...(f.source ? { source: f.source as LpsSource } : {}),
            ...(f.status ? { status: f.status as LpsLeadStatus } : {}),
            ...(f.forwardStatus ? { forwardStatus: f.forwardStatus as LpsForwardState } : {}),
            ...(f.connectionId ? { connectionId: f.connectionId } : {}),
            ...(f.destination ? { destination: f.destination } : {}),
            ...(f.agentId ? { agentId: f.agentId } : {}),
            ...(leadType !== 'all' ? { leadType } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(assignedFrom ? { assignedFrom } : {}),
            ...(assignedTo ? { assignedTo } : {}),
          };
        }}
        columns={columns}
        filters={filters}
        getRowId={(row) => row.id}
        onRowClick={(row) => setOpenId(row.id)}
        // Same flow as the Unassigned page: filter, tick rows, assign them all.
        bulkActions={
          canBulkAssign
            ? {
                extra: [
                  {
                    id: 'lps-bulk-assign',
                    label: 'Assign to agent',
                    onAction: (_ids, rows) => setBulkAssigning([...rows]),
                  },
                ],
              }
            : undefined
        }
        copy={{
          searchPlaceholder: 'Search name, phone, email, destination, CRM reference',
          emptyTitle: from ? 'No leads in this date range' : 'No inbound leads yet',
          emptyDescription: from
            ? 'Widen the range above, or pick All time, to see older leads.'
            : 'Leads show up here the moment a connected form, ad or WhatsApp number sends one.',
          errorTitle: 'Failed to load inbound leads',
          errorDescription: 'The server returned an error. Try refreshing.',
        }}
      />
      <LpsLeadDrawer
        leadId={openId}
        onClose={() => setOpenId(null)}
        canWrite={canWrite}
        onAssign={(lead) => setAssigning(lead)}
      />
      <AssignAgentDialog lead={assigning} onClose={() => setAssigning(null)} />
      <BulkAssignAgentDialog rows={bulkAssigning} onClose={() => setBulkAssigning(null)} />
    </>
  );
};
